import React, { useState, useEffect, useRef, useMemo, useCallback, CSSProperties } from "react";
import ReactDOM from "react-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Menu, MapPin, X, BarChart3, Clock, Phone, Mail, Star,
  ChevronDown, ChevronUp, Navigation, Building2, Calendar, DollarSign,
  Stethoscope, Filter, Route, Briefcase, Eye, EyeOff, Search, Layers,
  TrendingUp, Target, Activity, Zap, Globe, Car, Users, Info,
  Maximize, Minimize2
} from "lucide-react";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import { getStatusDisplay } from "./statusAbbreviations";
import IconButton from "@mui/material/IconButton";
import MuiSwitch from "@mui/material/Switch";
import Divider from "@mui/material/Divider";

export interface FacilityInfo {
  id: string;
  name: string;
  type: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  contactEmail: string;
  website: string;
  rating: number;
  bedCount: number;
  openPositions: number;
  requiredSkills: string[];
  specialties: string[];
  parkingInfo: string;
  orientation: string;
}

export interface PlacementRecord {
  id: string;
  status: "active" | "completed" | "pending" | "cancelled";
  /** Original API status for display. Used for abbreviation lookup fallback. */
  statusLabel?: string;
  activityStatusId?: number;
  activityTypeId?: number;
  startDate: string;
  endDate: string;
  role: string;
  skills: string[];
  payRate: number;
  shift: string;
  facility: FacilityInfo;
  orderPositions?: number;
}

export interface CandidateInfo {
  id: string;
  firstName: string;
  lastName: string;
  title: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  skills: string[];
  certifications: string[];
  experience: number;
  rating: number;
  availability: string;
  preferredShift: string;
  licensureState: string;
  npiNumber: string;
}

interface ResolvedFacilityInfo extends FacilityInfo {
  lat: number;
  lng: number;
}

interface ResolvedPlacementRecord extends Omit<PlacementRecord, "facility"> {
  facility: ResolvedFacilityInfo;
}

interface ResolvedCandidateInfo extends CandidateInfo {
  lat: number;
  lng: number;
}

interface ResolvedPlacementData {
  candidate: ResolvedCandidateInfo;
  placements: ResolvedPlacementRecord[];
}

export interface PlacementFilters {
  status: string[];
  maxDistance: number;
  sortBy: string;
  matchingOnly: boolean;
}

export interface PlacementData {
  candidate: CandidateInfo;
  placements: PlacementRecord[];
}

const geocodeCache: Record<string, { lat: number; lng: number }> = {};

async function geocodeAddress(address: string, city: string, state: string, zipCode: string): Promise<{ lat: number; lng: number }> {
  const cacheKey = `${address}|${city}|${state}|${zipCode}`;
  if (geocodeCache[cacheKey]) return geocodeCache[cacheKey];
  const queries = [
    `${address}, ${city}, ${state} ${zipCode}`,
    `${city}, ${state} ${zipCode}`,
    zipCode,
  ];
  for (const q of queries) {
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=1&countrycodes=us`, {
        headers: { "User-Agent": "PlacementTracker/1.0" },
      });
      if (!res.ok) continue;
      const data = await res.json();
      if (data && data.length > 0) {
        const result = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
        geocodeCache[cacheKey] = result;
        return result;
      }
    } catch { /* try next query */ }
  }
  const result = { lat: 29.76 + (Math.random() - 0.5) * 0.05, lng: -95.37 + (Math.random() - 0.5) * 0.05 };
  geocodeCache[cacheKey] = result;
  return result;
}

async function resolveAllCoordinates(data: PlacementData): Promise<ResolvedPlacementData> {
  const addressEntries: { key: string; address: string; city: string; state: string; zipCode: string }[] = [];
  addressEntries.push({ key: `candidate_${data.candidate.id}`, address: data.candidate.address, city: data.candidate.city, state: data.candidate.state, zipCode: data.candidate.zipCode });
  const seenFacilities = new Set<string>();
  for (const p of data.placements) {
    if (!seenFacilities.has(p.facility.id)) {
      seenFacilities.add(p.facility.id);
      addressEntries.push({ key: `facility_${p.facility.id}`, address: p.facility.address, city: p.facility.city, state: p.facility.state, zipCode: p.facility.zipCode });
    }
  }
  const coordsMap: Record<string, { lat: number; lng: number }> = {};
  const BATCH_SIZE = 2;
  for (let i = 0; i < addressEntries.length; i += BATCH_SIZE) {
    const batch = addressEntries.slice(i, i + BATCH_SIZE);
    const results = await Promise.all(batch.map(e => geocodeAddress(e.address, e.city, e.state, e.zipCode)));
    batch.forEach((e, idx) => { coordsMap[e.key] = results[idx]; });
    if (i + BATCH_SIZE < addressEntries.length) await new Promise(r => setTimeout(r, 1100));
  }
  const candidateCoords = coordsMap[`candidate_${data.candidate.id}`];
  const resolvedCandidate: ResolvedCandidateInfo = { ...data.candidate, lat: candidateCoords.lat, lng: candidateCoords.lng };
  const resolvedPlacements: ResolvedPlacementRecord[] = data.placements.map(p => {
    const fCoords = coordsMap[`facility_${p.facility.id}`];
    return { ...p, facility: { ...p.facility, lat: fCoords.lat, lng: fCoords.lng } };
  });
  return { candidate: resolvedCandidate, placements: resolvedPlacements };
}

async function fetchDrivingRoute(lat1: number, lng1: number, lat2: number, lng2: number): Promise<{ coordinates: [number, number][]; distanceMiles: number; durationMinutes: number } | null> {
  try {
    const res = await fetch(`https://router.project-osrm.org/route/v1/driving/${lng1},${lat1};${lng2},${lat2}?overview=full&geometries=geojson`);
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.routes || data.routes.length === 0) return null;
    const route = data.routes[0];
    const coords: [number, number][] = route.geometry.coordinates.map((c: number[]) => [c[1], c[0]]);
    return { coordinates: coords, distanceMiles: Math.round(route.distance / 1609.34 * 10) / 10, durationMinutes: Math.round(route.duration / 60) };
  } catch { return null; }
}

/** Set to true to show candidate and facility rating UI (hidden for future use). */
const SHOW_RATING_UI = false;

/** Set to true to show match percentage circle in facility drawer (hidden for now). */
const SHOW_COMPATIBILITY_GAUGE = false;

/** Set to true to show View Openings button in facility drawer (hidden for now). */
const SHOW_VIEW_OPENINGS_BUTTON = false;

let dynamicSkillColors: Record<string, string> = {};
function hslToHex(h: number, s: number, l: number): string {
  s /= 100; l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => { const k = (n + h / 30) % 12; const c = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1); return Math.round(255 * c).toString(16).padStart(2, "0"); };
  return `#${f(0)}${f(8)}${f(4)}`;
}
function buildSkillColorMap(data: PlacementData): Record<string, string> {
  const allNames = new Set<string>();
  data.candidate.skills.forEach(s => allNames.add(s));
  data.candidate.certifications.forEach(s => allNames.add(s));
  data.placements.forEach(p => {
    p.skills.forEach(s => allNames.add(s));
    p.facility.requiredSkills.forEach(s => allNames.add(s));
    p.facility.specialties.forEach(s => allNames.add(s));
  });
  allNames.delete("");
  const sorted = [...allNames].sort();
  const goldenAngle = 137.508;
  const map: Record<string, string> = {};
  sorted.forEach((name, i) => {
    const hue = (i * goldenAngle) % 360;
    const saturation = 60 + (i % 3) * 10;
    const lightness = 40 + (i % 4) * 5;
    map[name] = hslToHex(hue, saturation, lightness);
  });
  return map;
}

const MAP_STYLES = {
  light: { name: "Light", url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" },
  dark: { name: "Dark", url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" },
  voyager: { name: "Voyager", url: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" },
  satellite: { name: "Satellite", url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" },
  terrain: { name: "Terrain", url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png" },
};

function getSkillColor(skill: string): string {
  if (dynamicSkillColors[skill]) return dynamicSkillColors[skill];
  let hash = 0;
  for (let i = 0; i < skill.length; i++) { hash = ((hash << 5) - hash) + skill.charCodeAt(i); hash |= 0; }
  return hslToHex(Math.abs(hash) % 360, 65, 45);
}

function formatDisplayValue(
  value: string | number | null | undefined,
  type: 'date' | 'availability' | 'rating' | 'beds',
): string {
  if (value == null || value === '') return '—';
  if (type === 'availability') {
    const s = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const d = new Date(s);
      return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    return s;
  }
  if (type === 'date') {
    const d = new Date(String(value));
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  if (type === 'rating') return Number(value) > 0 ? String(value) : '—';
  if (type === 'beds') return Number(value) > 0 ? `${value} beds` : '—';
  return String(value);
}

function formatFullAddress(address: string, city: string, state: string, zipCode: string): string {
  const loc = [city, state, zipCode].filter(Boolean).join(', ');
  const parts = address ? [address, loc] : [loc];
  return parts.filter(Boolean).join(', ') || '—';
}

function formatDateRange(startDate: string, endDate: string): string {
  if (startDate && endDate) return `${startDate} to ${endDate}`;
  if (startDate) return formatDisplayValue(startDate, 'date');
  if (endDate) return formatDisplayValue(endDate, 'date');
  return '—';
}

function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3959;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * 10) / 10;
}

function getStatusColor(status: string): string {
  switch (status) {
    case "active": return "#059669";
    case "completed": return "#6B7280";
    case "pending": return "#D97706";
    case "cancelled": return "#DC2626";
    default: return "#6B7280";
  }
}

function getStatusStyle(status: string): CSSProperties {
  switch (status) {
    case "active": return { backgroundColor: "#ecfdf5", color: "#047857", border: "1px solid #a7f3d0" };
    case "completed": return { backgroundColor: "#f8fafc", color: "#475569", border: "1px solid #e2e8f0" };
    case "pending": return { backgroundColor: "#fffbeb", color: "#b45309", border: "1px solid #fde68a" };
    case "cancelled": return { backgroundColor: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" };
    default: return { backgroundColor: "#f8fafc", color: "#475569", border: "1px solid #e2e8f0" };
  }
}

/**
 * Parses a skill string from "discipline - specialty - subSpecialty" format (from API mapper).
 * Handles certifications (single token) and compound skills.
 */
function parseSkillParts(skill: string): { discipline: string; specialty: string; subSpecialty: string } {
  const parts = skill.split(' - ').map(p => p.trim().toLowerCase()).filter(Boolean);
  return {
    discipline: parts[0] || '',
    specialty: parts[1] || '',
    subSpecialty: parts[2] || '',
  };
}

/**
 * Match level: 2 = full (discipline+specialty), 1 = discipline-only, 0 = none.
 */
function getMatchLevel(candidateSkill: string, facilitySkill: string): 0 | 1 | 2 {
  if (!candidateSkill || !facilitySkill) return 0;
  if (candidateSkill.trim().toLowerCase() === facilitySkill.trim().toLowerCase()) return 2;
  const c = parseSkillParts(candidateSkill);
  const f = parseSkillParts(facilitySkill);
  if (!c.discipline || !f.discipline) return 0;
  if (c.discipline !== f.discipline) return 0;
  if (!f.specialty) return 2;
  if (c.specialty === f.specialty) return 2;
  return 1;
}

function skillMatchesByDisciplineSpecialty(candidateSkill: string, facilitySkill: string): boolean {
  return getMatchLevel(candidateSkill, facilitySkill) >= 1;
}

/** Returns facility required skills that are satisfied (full or discipline-only) by the candidate. */
function matchingSkills(candidateSkills: string[], facilitySkills: string[]): string[] {
  return facilitySkills.filter(fs =>
    candidateSkills.some(cs => skillMatchesByDisciplineSpecialty(cs, fs)),
  );
}

/**
 * Compatibility: full match = 100%, discipline-only = 50%, no match = 0%.
 * E.g. candidate "SLP - Home Health" vs facility "SLP - School" = 50% (discipline match).
 */
function calculateCompatibility(candidateSkills: string[], facilitySkills: string[]): number {
  if (facilitySkills.length === 0) return 0;
  let total = 0;
  for (const fs of facilitySkills) {
    let best = 0;
    for (const cs of candidateSkills) {
      const level = getMatchLevel(cs, fs);
      if (level === 2) { best = 100; break; }
      if (level === 1 && best < 50) best = 50;
    }
    total += best;
  }
  return Math.round(total / facilitySkills.length);
}

function deriveFacilities(placements: ResolvedPlacementRecord[]): ResolvedFacilityInfo[] {
  const seen = new Set<string>();
  const result: ResolvedFacilityInfo[] = [];
  for (const p of placements) {
    if (!seen.has(p.facility.id)) { seen.add(p.facility.id); result.push(p.facility); }
  }
  return result;
}

function getPlacementForFacility(placements: ResolvedPlacementRecord[], facilityId: string): ResolvedPlacementRecord | undefined {
  return placements.find(p => p.facility.id === facilityId);
}

function computeAnalytics(data: ResolvedPlacementData, allFacilities: ResolvedFacilityInfo[]) {
  const { placements, candidate } = data;
  const allSkills = [...candidate.skills, ...candidate.certifications];
  /** Active = On Assignment (23) only. Pending = all other POSITIVE. Cancelled = NEGATIVE only per enum. */
  const activePlacements = placements.filter(p => p.status === "active");
  const completedPlacements = placements.filter(p => p.status === "completed");
  const totalEarnings = completedPlacements.reduce((sum, p) => {
    if (!p.startDate || !p.endDate) return sum;
    const startMs = new Date(p.startDate).getTime();
    const endMs = new Date(p.endDate).getTime();
    if (isNaN(startMs) || isNaN(endMs)) return sum;
    const weeks = Math.ceil((endMs - startMs) / (7 * 24 * 60 * 60 * 1000));
    return sum + (p.payRate * 36 * Math.max(0, weeks));
  }, 0);
  const avgPayRate = placements.length > 0 ? Math.round(placements.reduce((s, p) => s + p.payRate, 0) / placements.length) : 0;
  const totalOpportunities = allFacilities.reduce((sum, f) => matchingSkills(allSkills, f.requiredSkills).length > 0 ? sum + f.openPositions : sum, 0);
  return {
    totalPlacements: placements.length, active: activePlacements.length, completed: completedPlacements.length,
    pending: placements.filter(p => p.status === "pending").length, totalEarnings, avgPayRate, totalOpportunities,
    facilitiesWithMatch: allFacilities.filter(f => matchingSkills(allSkills, f.requiredSkills).length > 0).length,
    totalFacilities: allFacilities.length,
  };
}

function AnimatedCounter({ target, prefix = "", suffix = "", duration = 1500 }: { target: number; prefix?: string; suffix?: string; duration?: number }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let startTime: number; let frame: number;
    const animate = (ts: number) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      setCount(Math.round((1 - Math.pow(1 - progress, 3)) * target));
      if (progress < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [target, duration]);
  return <span>{prefix}{count.toLocaleString()}{suffix}</span>;
}

const statColorMap: Record<string, { color: string; bg: string }> = {
  "Total Placements": { color: "#2563eb", bg: "#eff6ff" },
  "Active": { color: "#059669", bg: "#ecfdf5" },
  "Total Earned": { color: "#7c3aed", bg: "#f5f3ff" },
  "Avg Rate": { color: "#d97706", bg: "#fffbeb" },
  "Matching Facilities": { color: "#0891b2", bg: "#ecfeff" },
  "Open Opportunities": { color: "#e11d48", bg: "#fff1f2" },
};

function AnalyticsStripSection({ data, facilities }: { data: ResolvedPlacementData; facilities: ResolvedFacilityInfo[] }) {
  const analytics = computeAnalytics(data, facilities);
  const stats = [
    { icon: Briefcase, label: "Total Placements", value: analytics.totalPlacements },
    { icon: Activity, label: "Active", value: analytics.active },
    { icon: TrendingUp, label: "Avg Rate", value: analytics.avgPayRate, prefix: "$", suffix: "/hr" },
    { icon: Target, label: "Matching Facilities", value: analytics.facilitiesWithMatch, suffix: `/${analytics.totalFacilities}` },
    { icon: Zap, label: "Open Opportunities", value: analytics.totalOpportunities },
  ];
  return (
    <div style={{ flexShrink: 0, borderBottom: "1px solid #f1f5f9", background: "linear-gradient(to right, #f8fafc, #fff)", padding: "10px 16px" }} data-testid="analytics-strip">
      <div style={{ display: "flex", alignItems: "center", gap: 12, overflowX: "auto" }}>
        {stats.map((stat) => {
          const cm = statColorMap[stat.label] || { color: "#64748b", bg: "#f8fafc" };
          return (
            <div key={stat.label} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 12px", borderRadius: 8, backgroundColor: "#fff", border: "1px solid #f1f5f9", minWidth: "fit-content" }} data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, "-")}`}>
              <div style={{ width: 28, height: 28, borderRadius: 6, backgroundColor: cm.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <stat.icon size={14} color={cm.color} />
              </div>
              <div>
                <p style={{ fontSize: 10, color: "#94a3b8", fontWeight: 500, lineHeight: 1.2 }}>{stat.label}</p>
                <p style={{ fontSize: 14, fontWeight: 700, color: cm.color, lineHeight: 1.2 }}>
                  <AnimatedCounter target={stat.value} prefix={stat.prefix || ""} suffix={stat.suffix?.replace(/\/.*/, "") || ""} />
                  {stat.suffix?.includes("/") ? stat.suffix.slice(stat.suffix.indexOf("/")) : ""}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TimelineSection({ placements, onSelectFacility }: { placements: ResolvedPlacementRecord[]; onSelectFacility: (id: string) => void }) {
  const validPlacements = useMemo(
    () => placements.filter(p => p.startDate && p.endDate && !isNaN(new Date(p.startDate).getTime()) && !isNaN(new Date(p.endDate).getTime())),
    [placements],
  );
  const sortedPlacements = useMemo(() => [...validPlacements].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()), [validPlacements]);
  const timelineStart = useMemo(() => sortedPlacements[0] ? new Date(sortedPlacements[0].startDate) : new Date(), [sortedPlacements]);
  const timelineEnd = useMemo(() => sortedPlacements.length > 0 ? new Date(sortedPlacements[sortedPlacements.length - 1].endDate) : new Date(), [sortedPlacements]);
  const totalDays = Math.max((timelineEnd.getTime() - timelineStart.getTime()) / (24 * 60 * 60 * 1000), 1);
  const getPosition = (date: string) => ((new Date(date).getTime() - timelineStart.getTime()) / (24 * 60 * 60 * 1000) / totalDays) * 100;

  if (sortedPlacements.length === 0) {
    return (
      <div style={{ backgroundColor: "#fff", borderTop: "1px solid #f1f5f9", padding: "12px 16px" }} data-testid="placement-timeline">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Clock size={14} color="#94a3b8" />
          <span style={{ fontSize: 12, color: "#94a3b8" }}>No placement dates available for timeline</span>
        </div>
      </div>
    );
  }
  const getWidth = (start: string, end: string) => Math.max(((new Date(end).getTime() - new Date(start).getTime()) / (24 * 60 * 60 * 1000) / totalDays) * 100, 2);
  const formatMonth = (date: string) => new Date(date).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
  const statusColors: Record<string, string> = { active: "#059669", completed: "#94a3b8", pending: "#d97706", cancelled: "#ef4444" };
  const months = useMemo(() => {
    const result: { label: string; position: number }[] = [];
    const current = new Date(timelineStart);
    current.setDate(1);
    while (current <= timelineEnd) {
      const pos = ((current.getTime() - timelineStart.getTime()) / (24 * 60 * 60 * 1000)) / totalDays * 100;
      result.push({ label: current.toLocaleDateString("en-US", { month: "short", year: "2-digit" }), position: Math.max(0, Math.min(pos, 100)) });
      current.setMonth(current.getMonth() + 3);
    }
    return result;
  }, [timelineStart, timelineEnd, totalDays]);
  const today = new Date();
  const todayPos = ((today.getTime() - timelineStart.getTime()) / (24 * 60 * 60 * 1000)) / totalDays * 100;

  return (
    <div style={{ backgroundColor: "#fff", borderTop: "1px solid #f1f5f9" }} data-testid="placement-timeline">
      <div style={{ padding: "8px 16px", display: "flex", alignItems: "center", gap: 8 }}>
        <Clock size={14} color="#94a3b8" />
        <h3 style={{ fontSize: 12, fontWeight: 600, color: "#334155", margin: 0 }}>Placement Timeline</h3>
      </div>
      <div style={{ padding: "0 16px 12px" }}>
        <div style={{ position: "relative", height: 96, backgroundColor: "#f8fafc", borderRadius: 8, border: "1px solid #f1f5f9", overflow: "hidden" }}>
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 20, borderTop: "1px solid #f1f5f9", backgroundColor: "rgba(255,255,255,0.5)", display: "flex", alignItems: "center", padding: "0 8px" }}>
            {months.map((m, i) => (
              <span key={i} style={{ position: "absolute", fontSize: 9, color: "#94a3b8", fontWeight: 500, left: `${m.position}%`, transform: "translateX(-50%)" }}>{m.label}</span>
            ))}
          </div>
          {todayPos >= 0 && todayPos <= 100 && (
            <div style={{ position: "absolute", top: 0, bottom: 20, width: 1, backgroundColor: "#60a5fa", zIndex: 10, left: `${todayPos}%` }}>
              <div style={{ position: "absolute", top: -2, left: -8, backgroundColor: "#3b82f6", color: "#fff", fontSize: 8, fontWeight: 700, padding: "1px 4px", borderRadius: 3 }}>NOW</div>
            </div>
          )}
          {sortedPlacements.map((p, idx) => (
            <div key={p.id} style={{ position: "absolute", cursor: "pointer", left: `${getPosition(p.startDate)}%`, width: `${getWidth(p.startDate, p.endDate)}%`, top: `${8 + (idx % 3) * 20}px`, minWidth: 24 }} onClick={() => onSelectFacility(p.facility.id)} data-testid={`timeline-placement-${p.id}`}>
              <div style={{ height: 16, borderRadius: 9999, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: statusColors[p.status] + "25", border: `1.5px solid ${statusColors[p.status]}`, transition: "transform 0.2s" }}>
                <span style={{ fontSize: 8, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", padding: "0 6px", color: statusColors[p.status] }}>{p.facility.name.split(" ")[0]}</span>
              </div>
              <div className="timeline-tooltip" style={{ position: "absolute", top: -64, left: "50%", transform: "translateX(-50%)", backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 10px 15px rgba(0,0,0,0.1)", padding: 8, zIndex: 50, minWidth: 160, opacity: 0, pointerEvents: "none", transition: "opacity 0.2s" }}>
                <p style={{ fontSize: 10, fontWeight: 600, color: "#1e293b", margin: 0 }}>{p.role}</p>
                <p style={{ fontSize: 9, color: "#64748b", margin: 0 }}>{p.facility.name}</p>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, fontSize: 9, color: "#94a3b8" }}>
                  <span>{formatMonth(p.startDate)} - {formatMonth(p.endDate)}</span>
                  <span>${p.payRate}/hr</span>
                  <span title={getStatusDisplay(p.activityTypeId, p.activityStatusId, p.statusLabel, p.status).full}>{getStatusDisplay(p.activityTypeId, p.activityStatusId, p.statusLabel, p.status).abbr}</span>
                </div>
                <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                  {p.skills.map(s => <span key={s} style={{ fontSize: 8, padding: "1px 4px", borderRadius: 9999, color: "#fff", backgroundColor: getSkillColor(s) }}>{s}</span>)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <style>{`
        [data-testid^="timeline-placement-"]:hover .timeline-tooltip { opacity: 1 !important; }
        [data-testid^="timeline-placement-"]:hover > div:first-child { transform: scaleY(1.25); }
      `}</style>
    </div>
  );
}

function FacilityCard({ facility, distance, matching, compatibility, placement, isSelected, onSelect, onShowRoute, onViewDetails }: {
  facility: ResolvedFacilityInfo; distance: number; matching: string[]; compatibility: number; placement?: ResolvedPlacementRecord; isSelected: boolean; onSelect: () => void; onShowRoute: () => void; onViewDetails: () => void;
}) {
  return (
    <div
      style={{
        borderRadius: 8, border: isSelected ? "1px solid #93c5fd" : "1px solid #f1f5f9", padding: 14, cursor: "pointer",
        transition: "all 0.2s", backgroundColor: isSelected ? "rgba(239,246,255,0.5)" : "#fff",
        boxShadow: isSelected ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
      }}
      onClick={onSelect} data-testid={`card-facility-${facility.id}`}
      onMouseEnter={e => { if (!isSelected) { (e.currentTarget as HTMLDivElement).style.borderColor = "#e2e8f0"; (e.currentTarget as HTMLDivElement).style.boxShadow = "0 1px 2px rgba(0,0,0,0.05)"; } }}
      onMouseLeave={e => { if (!isSelected) { (e.currentTarget as HTMLDivElement).style.borderColor = "#f1f5f9"; (e.currentTarget as HTMLDivElement).style.boxShadow = "none"; } }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Building2 size={16} color="#2563eb" style={{ flexShrink: 0 }} />
            <h4 style={{ fontSize: 14, fontWeight: 600, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", margin: 0 }}>{facility.name}</h4>
          </div>
          <p style={{ fontSize: 10, color: "#64748b", marginTop: 2, marginLeft: 24, marginBottom: 0 }}>{facility.type}</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#64748b" }}>
            <Navigation size={12} />
            <span style={{ fontWeight: 500 }}>{distance} mi</span>
          </div>
          <div style={{
            fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 9999,
            backgroundColor: compatibility >= 70 ? "#dcfce7" : compatibility >= 40 ? "#fef3c7" : "#fee2e2",
            color: compatibility >= 70 ? "#059669" : compatibility >= 40 ? "#d97706" : "#dc2626",
          }} data-testid={`compatibility-${facility.id}`}>
            {compatibility}% match
          </div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8, marginLeft: 24, fontSize: 12, color: "#64748b" }}>
        {SHOW_RATING_UI && (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}><Star size={12} color="#fbbf24" fill="#fbbf24" /><span>{formatDisplayValue(facility.rating, 'rating')}</span></div>
        )}
        {facility.bedCount > 0 && <span>{facility.bedCount} beds</span>}
        <span style={{ color: "#2563eb", fontWeight: 500 }}>{facility.openPositions} open</span>
        {placement?.orderPositions != null && placement.orderPositions > 0 && <span style={{ color: "#7c3aed", fontWeight: 500 }}>{placement.orderPositions} order pos.</span>}
      </div>
      {placement && (
        <div style={{ marginTop: 10, marginLeft: 24, padding: 10, backgroundColor: "#f8fafc", borderRadius: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "nowrap", minWidth: 0 }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: "0 1 auto", minWidth: 0 }}>{placement.role}</span>
            <Tooltip title={getStatusDisplay(placement.activityTypeId, placement.activityStatusId, placement.statusLabel, placement.status).full} arrow>
              <span style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", padding: "2px 6px", borderRadius: 9999, whiteSpace: "nowrap", flexShrink: 0, ...getStatusStyle(placement.status) }}>{getStatusDisplay(placement.activityTypeId, placement.activityStatusId, placement.statusLabel, placement.status).abbr}</span>
            </Tooltip>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 6, fontSize: 11, color: "#64748b", flexWrap: "wrap" }}>
            {(placement.startDate || placement.endDate) && (
              <div style={{ display: "flex", alignItems: "center", gap: 2 }}><Calendar size={12} />{formatDateRange(placement.startDate, placement.endDate)}</div>
            )}
            {(placement.payRate != null && placement.payRate > 0) && (
              <div style={{ display: "flex", alignItems: "center", gap: 2 }}><DollarSign size={12} />${placement.payRate}/hr</div>
            )}
            {placement.shift && <div style={{ display: "flex", alignItems: "center", gap: 2 }}><Clock size={12} />{placement.shift}</div>}
          </div>
          {placement.skills.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
              {placement.skills.map(s => <span key={s} style={{ fontSize: 10, padding: "1px 6px", borderRadius: 9999, color: "#fff", fontWeight: 500, backgroundColor: getSkillColor(s) }}>{s}</span>)}
            </div>
          )}
        </div>
      )}
      {!placement && matching.length > 0 && (
        <div style={{ marginTop: 8, marginLeft: 24 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {matching.slice(0, 5).map(s => <span key={s} style={{ fontSize: 10, padding: "1px 6px", borderRadius: 9999, fontWeight: 500, color: getSkillColor(s), border: `1px solid ${getSkillColor(s)}40`, backgroundColor: getSkillColor(s) + "10" }}>{s}</span>)}
            {matching.length > 5 && <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 500 }}>+{matching.length - 5}</span>}
          </div>
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10, marginLeft: 24 }}>
        <Button variant="outlined" size="small" sx={{ fontSize: 10, height: 24, gap: 0.5, px: 1, textTransform: "none", borderColor: "#e2e8f0", color: "#334155" }} onClick={(e) => { e.stopPropagation(); onShowRoute(); }} data-testid={`button-route-${facility.id}`}>
          <Route size={12} />Route
        </Button>
        <Button variant="text" size="small" sx={{ fontSize: 10, height: 24, gap: 0.5, px: 1, textTransform: "none", color: "#2563eb" }} onClick={(e) => { e.stopPropagation(); onViewDetails(); }} data-testid={`button-details-${facility.id}`}>
          <Eye size={12} />Details
        </Button>
      </div>
    </div>
  );
}

function CandidatePanelSection({ data, facilities, placements, selectedFacility, onSelectFacility, onShowRoute, onDrawerOpen, filters, onFiltersChange, activeSkills, onActiveSkillsChange, embedded = false }: {
  data: ResolvedPlacementData; facilities: ResolvedFacilityInfo[]; placements: ResolvedPlacementRecord[]; selectedFacility: string | null; onSelectFacility: (id: string | null) => void; onShowRoute: (id: string) => void; onDrawerOpen: (id: string) => void;
  filters: PlacementFilters; onFiltersChange: (f: PlacementFilters) => void; activeSkills: string[]; onActiveSkillsChange: (s: string[]) => void; embedded?: boolean;
}) {
  const [profileExpanded, setProfileExpanded] = useState(!embedded);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { candidate } = data;
  const allSkills = [...candidate.skills, ...candidate.certifications];

  const facilityDistances = facilities.map(f => ({
    ...f, distance: calculateDistance(candidate.lat, candidate.lng, f.lat, f.lng),
    matching: matchingSkills(allSkills, f.requiredSkills), compatibility: calculateCompatibility(allSkills, f.requiredSkills),
    placement: getPlacementForFacility(placements, f.id),
  }));

  let filteredFacilities = facilityDistances.filter(f => {
    if (filters.maxDistance < 100 && f.distance > filters.maxDistance) return false;
    if (filters.matchingOnly && f.matching.length === 0) return false;
    if (filters.status.length > 0 && f.placement && !filters.status.includes(f.placement.status)) return false;
    if (filters.status.length > 0 && !f.placement) return false;
    return true;
  });

  filteredFacilities.sort((a, b) => {
    switch (filters.sortBy) {
      case "nearest": return a.distance - b.distance;
      case "farthest": return b.distance - a.distance;
      case "matching": return b.compatibility - a.compatibility;
      case "rating": return b.rating - a.rating;
      default: return a.distance - b.distance;
    }
  });

  const activeFiltersCount = (filters.status.length > 0 ? 1 : 0) + (filters.maxDistance < 100 ? 1 : 0) + (filters.sortBy !== "nearest" ? 1 : 0) + (filters.matchingOnly ? 1 : 0);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", backgroundColor: "#fff" }} data-testid="candidate-panel">
      <div style={{ padding: "20px 20px 16px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
          <div style={{ width: 56, height: 56, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #4f46e5)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 18, fontWeight: 600, flexShrink: 0, boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }} data-testid="avatar-candidate">
            {candidate.firstName[0]}{candidate.lastName[0]}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2 style={{ fontSize: 18, fontWeight: 600, color: "#0f172a", lineHeight: 1.2, margin: 0 }} data-testid="text-candidate-name">{candidate.firstName} {candidate.lastName}</h2>
            <p style={{ fontSize: 14, color: "#64748b", marginTop: 2, marginBottom: 0 }}>{candidate.title}</p>
            <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4, flexWrap: "wrap" }}>
              {SHOW_RATING_UI && (
                <>
                  <Star size={14} color="#fbbf24" fill="#fbbf24" />
                  <span style={{ fontSize: 14, fontWeight: 500, color: "#334155" }}>{formatDisplayValue(candidate.rating, 'rating')}</span>
                  <span style={{ fontSize: 14, color: "#cbd5e1", margin: "0 4px" }}>|</span>
                </>
              )}
              <Clock size={14} color="#94a3b8" />
              <span style={{ fontSize: 14, color: "#64748b" }}>{candidate.experience} yrs</span>
              <span style={{ fontSize: 14, color: "#cbd5e1", margin: "0 4px" }}>|</span>
              <span style={{ fontSize: 12, padding: "2px 6px", backgroundColor: "#ecfdf5", color: "#059669", borderRadius: 9999, fontWeight: 500, border: "1px solid #a7f3d0" }}>{formatDisplayValue(candidate.availability, 'availability')}</span>
            </div>
          </div>
        </div>
        <Button variant="text" size="small" sx={{ fontSize: 12, color: "#2563eb", gap: 0.5, mt: 1.5, px: 0, textTransform: "none", minWidth: "auto" }} onClick={() => setProfileExpanded(!profileExpanded)} data-testid="button-toggle-profile">
          {profileExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          {profileExpanded ? "Hide Details" : "Show Details"}
        </Button>
        {profileExpanded && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#475569" }}><MapPin size={14} color="#94a3b8" style={{ flexShrink: 0 }} /><span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{candidate.city}, {candidate.state}</span></div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#475569" }}><Phone size={14} color="#94a3b8" style={{ flexShrink: 0 }} /><span>{candidate.phone}</span></div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#475569" }}><Mail size={14} color="#94a3b8" style={{ flexShrink: 0 }} /><span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{candidate.email}</span></div>
              {candidate.preferredShift && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#475569" }}><Briefcase size={14} color="#94a3b8" style={{ flexShrink: 0 }} /><span>{candidate.preferredShift}</span></div>
              )}
            </div>
            <div style={{ paddingTop: 8 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <p style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", margin: 0 }}>Clinical Skills</p>
                {activeSkills.length > 0 && <button style={{ fontSize: 10, color: "#3b82f6", fontWeight: 500, border: "none", background: "none", cursor: "pointer" }} onClick={() => onActiveSkillsChange([])} data-testid="button-clear-active-skills">Clear</button>}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {candidate.skills.map(s => {
                  const isActive = activeSkills.includes(s);
                  return (
                    <button key={s} style={{
                      display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: 9999, fontSize: 10, fontWeight: 600,
                      color: "#fff", backgroundColor: getSkillColor(s), border: "none", cursor: "pointer",
                      transition: "all 0.2s", opacity: activeSkills.length > 0 && !isActive ? 0.4 : 1,
                      boxShadow: isActive ? `0 4px 6px rgba(0,0,0,0.1), 0 0 0 2px #fff, 0 0 0 4px ${getSkillColor(s)}` : "0 1px 2px rgba(0,0,0,0.05)",
                    }}
                    onClick={() => isActive ? onActiveSkillsChange(activeSkills.filter(sk => sk !== s)) : onActiveSkillsChange([...activeSkills, s])}
                    data-testid={`badge-skill-${s}`}>{s}</button>
                  );
                })}
              </div>
            </div>
            <div style={{ paddingTop: 4 }}>
              <p style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6, marginTop: 0 }}>Certifications</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {candidate.certifications.map(c => {
                  const isActive = activeSkills.includes(c);
                  const certColor = getSkillColor(c);
                  return (
                    <button key={c} style={{
                      display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: 9999, fontSize: 10, fontWeight: 600,
                      border: `1px solid ${certColor}`,
                      color: isActive ? "#fff" : certColor,
                      backgroundColor: isActive ? certColor : `${certColor}15`,
                      cursor: "pointer", transition: "all 0.2s",
                      opacity: activeSkills.length > 0 && !isActive ? 0.4 : 1,
                      boxShadow: isActive ? `0 4px 6px rgba(0,0,0,0.1), 0 0 0 2px #fff, 0 0 0 4px ${certColor}` : "none",
                    }}
                    onClick={() => isActive ? onActiveSkillsChange(activeSkills.filter(sk => sk !== c)) : onActiveSkillsChange([...activeSkills, c])}
                    data-testid={`badge-cert-${c}`}>{c}</button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
      <Divider />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          <div style={{ padding: "8px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <span style={{ fontSize: 12, color: "#64748b" }}>{filteredFacilities.length} facilities</span>
            <Button variant="outlined" size="small" sx={{ fontSize: 12, gap: 0.75, textTransform: "none", borderColor: filtersOpen ? "#bfdbfe" : "#e2e8f0", color: filtersOpen ? "#1d4ed8" : "#334155", backgroundColor: filtersOpen ? "#eff6ff" : "transparent" }} onClick={() => setFiltersOpen(!filtersOpen)} data-testid="button-toggle-filters">
              <Filter size={14} />Filters
              {activeFiltersCount > 0 && <span style={{ marginLeft: 2, width: 16, height: 16, borderRadius: "50%", backgroundColor: "#2563eb", color: "#fff", fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>{activeFiltersCount}</span>}
            </Button>
          </div>
          {filtersOpen && (
            <div style={{ padding: "0 20px 12px" }} data-testid="filters-panel">
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 6 }}>Status</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {(["active", "completed", "pending", "cancelled"] as const).map(status => (
                    <Button key={status} variant="outlined" size="small"
                      sx={{ fontSize: 10, height: 24, px: 1, textTransform: "none", borderColor: filters.status.includes(status) ? getStatusStyle(status).border : "#e2e8f0", color: filters.status.includes(status) ? getStatusStyle(status).color : "#334155", backgroundColor: filters.status.includes(status) ? getStatusStyle(status).backgroundColor : "transparent" }}
                      onClick={() => { const ns = filters.status.includes(status) ? filters.status.filter(s => s !== status) : [...filters.status, status]; onFiltersChange({ ...filters, status: ns }); }}
                      data-testid={`filter-status-${status}`}>{status.charAt(0).toUpperCase() + status.slice(1)}</Button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 6 }}>Distance: {filters.maxDistance >= 100 ? "Any" : `${filters.maxDistance} mi`}</label>
                <input type="range" min="1" max="100" value={filters.maxDistance} onChange={e => onFiltersChange({ ...filters, maxDistance: parseInt(e.target.value) })} style={{ width: "100%", height: 6, borderRadius: 9999, appearance: "auto", cursor: "pointer", accentColor: "#2563eb" }} data-testid="input-distance-slider" />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 6 }}>Sort</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {[{ key: "nearest", label: "Nearest" }, { key: "farthest", label: "Farthest" }, { key: "matching", label: "Best Match" }, ...(SHOW_RATING_UI ? [{ key: "rating", label: "Rating" }] : [])].map((opt: { key: string; label: string }) => (
                    <Button key={opt.key} variant="outlined" size="small"
                      sx={{ fontSize: 10, height: 24, px: 1, textTransform: "none", borderColor: filters.sortBy === opt.key ? "#bfdbfe" : "#e2e8f0", color: filters.sortBy === opt.key ? "#1d4ed8" : "#334155", backgroundColor: filters.sortBy === opt.key ? "#eff6ff" : "transparent" }}
                      onClick={() => onFiltersChange({ ...filters, sortBy: opt.key })} data-testid={`filter-sort-${opt.key}`}>{opt.label}</Button>
                  ))}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <label style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Matching Only</label>
                <MuiSwitch size="small" checked={filters.matchingOnly} onChange={(_, checked) => onFiltersChange({ ...filters, matchingOnly: checked })} data-testid="toggle-matching-only" />
              </div>
              {activeFiltersCount > 0 && (
                <Button variant="text" size="small" sx={{ fontSize: 12, color: "#ef4444", gap: 0.5, px: 0, mt: 1, textTransform: "none" }} onClick={() => onFiltersChange({ status: [], maxDistance: 100, sortBy: "nearest", matchingOnly: false })} data-testid="button-clear-filters">
                  <X size={12} /> Clear Filters
                </Button>
              )}
            </div>
          )}
          {filtersOpen && <Divider />}
          <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
            <div style={{ padding: "8px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
              {filteredFacilities.map(f => (
                <FacilityCard key={f.id} facility={f} distance={f.distance} matching={f.matching} compatibility={f.compatibility} placement={f.placement}
                  isSelected={selectedFacility === f.id} onSelect={() => onSelectFacility(selectedFacility === f.id ? null : f.id)}
                  onShowRoute={() => onShowRoute(f.id)} onViewDetails={() => onDrawerOpen(f.id)} />
              ))}
              {filteredFacilities.length === 0 && (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }} data-testid="text-no-results">
                  <Stethoscope size={40} style={{ margin: "0 auto 8px", opacity: 0.4, display: "block" }} />
                  <p style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>No facilities match your filters</p>
                  <p style={{ fontSize: 12, marginTop: 4, marginBottom: 0 }}>Try adjusting your filter criteria</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CollapsibleChips({ label, expanded, onToggle, children, style }: { label: string; expanded: boolean; onToggle: () => void; children: React.ReactNode; style?: CSSProperties }) {
  const innerRef = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(false);
  const LINE_HEIGHT = 22;
  const MAX_COLLAPSED = LINE_HEIGHT * 2 + 4;
  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const check = () => setOverflows(el.scrollHeight > MAX_COLLAPSED + 2);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [children]);
  return (
    <div style={style}>
      <p style={{ fontSize: 10, color: "#94a3b8", marginBottom: 4, marginTop: 0 }}>{label}</p>
      <div style={{ maxHeight: expanded ? undefined : MAX_COLLAPSED, overflow: "hidden", transition: "max-height 0.25s ease" }}>
        <div ref={innerRef} style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>{children}</div>
      </div>
      {overflows && (
        <button onClick={onToggle} data-testid={`toggle-${label.replace(/\s+/g, "-").toLowerCase()}`}
          style={{ background: "none", border: "none", padding: 0, marginTop: 4, fontSize: 10, color: "#3b82f6", cursor: "pointer", fontWeight: 500 }}>
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

function FacilityDrawerSection({ facilityId, candidate, facilities, placements, onClose, onShowRoute }: {
  facilityId: string; candidate: ResolvedCandidateInfo; facilities: ResolvedFacilityInfo[]; placements: ResolvedPlacementRecord[]; onClose: () => void; onShowRoute: (id: string) => void;
}) {
  const [specExpanded, setSpecExpanded] = useState(false);
  const [skillsExpanded, setSkillsExpanded] = useState(false);
  const [driveInfo, setDriveInfo] = useState<{ distance: number; time: number } | null>(null);
  const facility = facilities.find(f => f.id === facilityId);
  useEffect(() => {
    const f = facilities.find(x => x.id === facilityId);
    if (!f) return;
    setDriveInfo(null);
    fetchDrivingRoute(candidate.lat, candidate.lng, f.lat, f.lng).then(route => {
      if (route) setDriveInfo({ distance: route.distanceMiles, time: route.durationMinutes });
    });
  }, [facilityId, candidate.lat, candidate.lng, facilities]);
  if (!facility) return null;
  const allSkills = [...candidate.skills, ...candidate.certifications];
  const matching = matchingSkills(allSkills, facility.requiredSkills);
  const compatibility = calculateCompatibility(allSkills, facility.requiredSkills);
  const straightDistance = calculateDistance(candidate.lat, candidate.lng, facility.lat, facility.lng);
  const placement = getPlacementForFacility(placements, facilityId);
  const distance = driveInfo ? driveInfo.distance : straightDistance;
  const estDriveTime = driveInfo ? driveInfo.time : Math.round(straightDistance * 2.5);
  const circumference = 2 * Math.PI * 38;
  const offset = circumference - (compatibility / 100) * circumference;

  return (
    <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, zIndex: 600, animation: "slideUp 0.3s ease-out" }} data-testid="facility-drawer">
      <div style={{ backgroundColor: "#fff", borderRadius: "16px 16px 0 0", boxShadow: "0 -4px 20px rgba(0,0,0,0.15)", border: "1px solid #e2e8f0", borderBottom: "none", maxHeight: "55vh", overflowY: "auto" }}>
        <div style={{ position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 10, padding: "16px 20px 12px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, borderBottom: "1px solid #f1f5f9" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: "linear-gradient(135deg, #3b82f6, #4f46e5)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Building2 size={20} color="#fff" /></div>
            <div style={{ minWidth: 0 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", margin: 0 }} data-testid="drawer-facility-name">{facility.name}</h3>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#64748b" }}>
                <span>{facility.type}</span>
                {SHOW_RATING_UI && (
                  <>
                    <span style={{ color: "#cbd5e1" }}>|</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 2 }}><Star size={12} color="#fbbf24" fill="#fbbf24" /><span style={{ fontWeight: 500 }}>{formatDisplayValue(facility.rating, 'rating')}</span></div>
                  </>
                )}
                {facility.bedCount > 0 && (
                  <>
                    <span style={{ color: "#cbd5e1" }}>|</span>
                    <span>{facility.bedCount} beds</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {SHOW_COMPATIBILITY_GAUGE && (
              <div style={{ position: "relative", width: 64, height: 64, flexShrink: 0 }} data-testid="compatibility-gauge">
                <svg width="64" height="64" viewBox="0 0 84 84" style={{ transform: "rotate(-90deg)" }}>
                  <circle cx="42" cy="42" r="38" fill="none" stroke="#f1f5f9" strokeWidth="5" />
                  <circle cx="42" cy="42" r="38" fill="none" stroke={compatibility >= 70 ? "#059669" : compatibility >= 40 ? "#d97706" : "#dc2626"} strokeWidth="5" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset} style={{ transition: "all 1s ease-out" }} />
                </svg>
                <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>{compatibility}%</span>
                  <span style={{ fontSize: 8, color: "#94a3b8", fontWeight: 500 }}>Match</span>
                </div>
              </div>
            )}
            <IconButton size="small" onClick={onClose} data-testid="button-close-drawer"><X size={16} /></IconButton>
          </div>
        </div>
        <div style={{ padding: "16px 20px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
          <div>
            <h4 style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 12px" }}>Contact</h4>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {(facility.address || facility.city || facility.state || facility.zipCode) && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#475569" }}><MapPin size={14} color="#94a3b8" style={{ flexShrink: 0 }} /><span>{formatFullAddress(facility.address, facility.city, facility.state, facility.zipCode)}</span></div>
              )}
              {facility.phone && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#475569" }}><Phone size={14} color="#94a3b8" style={{ flexShrink: 0 }} /><span>{facility.phone}</span></div>
              )}
              {facility.contactEmail && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#475569" }}><Mail size={14} color="#94a3b8" style={{ flexShrink: 0 }} /><span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{facility.contactEmail}</span></div>
              )}
              {facility.website && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#475569" }}><Globe size={14} color="#94a3b8" style={{ flexShrink: 0 }} /><span>{facility.website}</span></div>
              )}
            </div>
          </div>
          <div>
            <h4 style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 12px" }}>Details</h4>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#475569" }}><Navigation size={14} color="#94a3b8" style={{ flexShrink: 0 }} /><span>{distance} miles ({estDriveTime >= 60 ? `${Math.floor(estDriveTime / 60)}h ${estDriveTime % 60}m` : `${estDriveTime} min`} drive{driveInfo ? "" : " est."})</span></div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#475569" }}><Users size={14} color="#3b82f6" style={{ flexShrink: 0 }} /><span style={{ fontWeight: 500, color: "#2563eb" }}>{facility.openPositions} open positions</span>{placement?.orderPositions != null && placement.orderPositions > 0 && <span style={{ fontWeight: 500, color: "#7c3aed", marginLeft: 4 }}>| {placement.orderPositions} order positions</span>}</div>
              {facility.parkingInfo && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#475569" }}><Car size={14} color="#94a3b8" style={{ flexShrink: 0 }} /><span>{facility.parkingInfo}</span></div>
              )}
              {facility.orientation && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#475569" }}><Clock size={14} color="#94a3b8" style={{ flexShrink: 0 }} /><span>{facility.orientation}</span></div>
              )}
            </div>
          </div>
          <div>
            <h4 style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 12px" }}>Skills & Specialties</h4>
            <CollapsibleChips label="Specialties" expanded={specExpanded} onToggle={() => setSpecExpanded(!specExpanded)}>
              {facility.specialties.map(s => { const sc = getSkillColor(s); return <span key={s} style={{ fontSize: 10, padding: "2px 6px", backgroundColor: `${sc}15`, border: `1px solid ${sc}40`, borderRadius: 9999, color: sc, fontWeight: 500 }}>{s}</span>; })}
            </CollapsibleChips>
            <CollapsibleChips label={`Required Skills (${matching.length}/${facility.requiredSkills.length} matched)`} expanded={skillsExpanded} onToggle={() => setSkillsExpanded(!skillsExpanded)} style={{ marginTop: 12 }}>
              {facility.requiredSkills.map(s => {
                const isMatch = matching.includes(s);
                const skc = getSkillColor(s);
                return <span key={s} style={{ fontSize: 10, padding: "2px 6px", borderRadius: 9999, fontWeight: 500, ...(isMatch ? { color: "#fff", backgroundColor: skc } : { color: skc, backgroundColor: `${skc}15`, border: `1px solid ${skc}40`, opacity: 0.7 }) }}>{s}</span>;
              })}
            </CollapsibleChips>
          </div>
        </div>
        {placement && (
          <>
            <Divider />
            <div style={{ padding: "12px 20px" }}>
              <h4 style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8, marginTop: 0 }}>Current Placement</h4>
              <div style={{ display: "flex", alignItems: "center", gap: 12, backgroundColor: "#f8fafc", borderRadius: 8, padding: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "nowrap", minWidth: 0 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: "0 1 auto", minWidth: 0 }}>{placement.role}</span>
                    <span style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", padding: "2px 6px", borderRadius: 9999, whiteSpace: "nowrap", flexShrink: 0, ...getStatusStyle(placement.status) }}>{getStatusDisplay(placement.activityTypeId, placement.activityStatusId, placement.statusLabel, placement.status).full}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 12, color: "#64748b", marginTop: 4, flexWrap: "wrap" }}>
                    {(placement.startDate || placement.endDate) && (
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}><Calendar size={14} color="#94a3b8" style={{ flexShrink: 0 }} />{formatDateRange(placement.startDate, placement.endDate)}</div>
                    )}
                    {(placement.payRate != null && placement.payRate > 0) && (
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}><DollarSign size={14} color="#94a3b8" style={{ flexShrink: 0 }} />${placement.payRate}/hr</div>
                    )}
                    {placement.shift && (
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}><Clock size={14} color="#94a3b8" style={{ flexShrink: 0 }} />{placement.shift}</div>
                    )}
                  </div>
                </div>
                {placement.skills.length > 0 && (
                  <div style={{ display: "flex", gap: 4 }}>{placement.skills.map(s => <span key={s} style={{ fontSize: 10, padding: "2px 6px", borderRadius: 9999, color: "#fff", fontWeight: 500, backgroundColor: getSkillColor(s) }}>{s}</span>)}</div>
                )}
              </div>
            </div>
          </>
        )}
        <div style={{ padding: "0 20px 16px", display: "flex", alignItems: "center", gap: 8 }}>
          <Button variant="contained" size="small" sx={{ gap: 0.75, textTransform: "none", backgroundColor: "#3b82f6", "&:hover": { backgroundColor: "#2563eb" } }} onClick={() => onShowRoute(facilityId)} data-testid="drawer-button-route"><Navigation size={14} />Show Route</Button>
          {SHOW_VIEW_OPENINGS_BUTTON && (
            <Button variant="outlined" size="small" sx={{ gap: 0.75, textTransform: "none", borderColor: "#e2e8f0", color: "#334155" }} data-testid="drawer-button-apply"><Stethoscope size={14} />View Openings</Button>
          )}
        </div>
      </div>
      <style>{`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
    </div>
  );
}

function MapControlsSection({ facilities, candidate, onSelectFacility, onStyleChange, currentStyle }: {
  facilities: ResolvedFacilityInfo[]; candidate: ResolvedCandidateInfo; onSelectFacility: (id: string) => void; onStyleChange: (style: keyof typeof MAP_STYLES) => void; currentStyle: keyof typeof MAP_STYLES;
}) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [styleOpen, setStyleOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (searchOpen && inputRef.current) inputRef.current.focus(); }, [searchOpen]);
  const filteredFacilities =
    query.length > 0
      ? facilities.filter(
          f =>
            f.name.toLowerCase().includes(query.toLowerCase()) ||
            f.type.toLowerCase().includes(query.toLowerCase()) ||
            f.requiredSkills.some(s => s.toLowerCase().includes(query.toLowerCase())) ||
            f.specialties.some(s => s.toLowerCase().includes(query.toLowerCase())),
        )
      : [];

  return (
    <>
      <div style={{ position: "absolute", top: 16, right: 64, zIndex: 500, display: "flex", flexDirection: "column", gap: 8 }} data-testid="map-controls">
        <div style={{ position: "relative" }}>
          {searchOpen ? (
            <div style={{ backgroundColor: "#fff", borderRadius: 8, boxShadow: "0 10px 15px rgba(0,0,0,0.1)", border: "1px solid #e2e8f0", width: 288 }} data-testid="search-panel">
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderBottom: "1px solid #f1f5f9" }}>
                <Search size={16} color="#94a3b8" />
                <input ref={inputRef} type="text" placeholder="Search facilities, skills..." value={query} onChange={e => setQuery(e.target.value)} style={{ flex: 1, fontSize: 14, color: "#1e293b", border: "none", outline: "none", backgroundColor: "transparent", fontFamily: "inherit" }} data-testid="input-search" />
                <IconButton size="small" onClick={() => { setSearchOpen(false); setQuery(""); }} data-testid="button-close-search"><X size={14} /></IconButton>
              </div>
              {filteredFacilities.length > 0 && (
                <div style={{ maxHeight: 240, overflowY: "auto" }}>
                  {filteredFacilities.map(f => (
                    <button key={f.id} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", border: "none", backgroundColor: "transparent", cursor: "pointer", textAlign: "left", fontFamily: "inherit", transition: "background 0.15s" }}
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = "#f8fafc")}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = "transparent")}
                      onClick={() => { onSelectFacility(f.id); setSearchOpen(false); setQuery(""); }} data-testid={`search-result-${f.id}`}>
                      <MapPin size={14} color="#3b82f6" style={{ flexShrink: 0 }} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <p style={{ fontSize: 12, fontWeight: 500, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", margin: 0 }}>{f.name}</p>
                        <p style={{ fontSize: 10, color: "#94a3b8", margin: 0 }}>{f.type} | {calculateDistance(candidate.lat, candidate.lng, f.lat, f.lng)} mi</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {query.length > 0 && filteredFacilities.length === 0 && <div style={{ padding: "16px 12px", textAlign: "center", fontSize: 12, color: "#94a3b8" }}>No facilities found</div>}
            </div>
          ) : (
            <IconButton sx={{ backgroundColor: "#fff", boxShadow: "0 4px 6px rgba(0,0,0,0.1)", border: "1px solid #e2e8f0", "&:hover": { backgroundColor: "#f8fafc" } }} onClick={() => setSearchOpen(true)} data-testid="button-open-search"><Search size={16} /></IconButton>
          )}
        </div>
        <div style={{ position: "relative" }}>
          <IconButton sx={{ backgroundColor: styleOpen ? "#eff6ff" : "#fff", boxShadow: "0 4px 6px rgba(0,0,0,0.1)", border: `1px solid ${styleOpen ? "#bfdbfe" : "#e2e8f0"}`, "&:hover": { backgroundColor: "#f8fafc" } }} onClick={() => setStyleOpen(!styleOpen)} data-testid="button-map-style"><Layers size={16} /></IconButton>
          {styleOpen && (
            <div style={{ position: "absolute", right: 0, top: 44, backgroundColor: "#fff", borderRadius: 8, boxShadow: "0 10px 15px rgba(0,0,0,0.1)", border: "1px solid #e2e8f0", padding: 8, minWidth: 140 }} data-testid="style-panel">
              {(Object.keys(MAP_STYLES) as (keyof typeof MAP_STYLES)[]).map(key => (
                <button key={key} style={{
                  width: "100%", textAlign: "left", padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 500, border: "none", cursor: "pointer", fontFamily: "inherit",
                  backgroundColor: currentStyle === key ? "#eff6ff" : "transparent",
                  color: currentStyle === key ? "#1d4ed8" : "#475569",
                  transition: "background 0.15s",
                }}
                onMouseEnter={e => { if (currentStyle !== key) e.currentTarget.style.backgroundColor = "#f8fafc"; }}
                onMouseLeave={e => { if (currentStyle !== key) e.currentTarget.style.backgroundColor = "transparent"; }}
                onClick={() => { onStyleChange(key); setStyleOpen(false); }} data-testid={`style-option-${key}`}>{MAP_STYLES[key].name}</button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function MapSection({ data, facilities, placements, selectedFacility, onSelectFacility, routeFacilityId, expandedFacility, onExpandFacility, onShowRoute, filters, drawerFacilityId, onDrawerOpen, activeSkills, showLegend }: {
  data: ResolvedPlacementData; facilities: ResolvedFacilityInfo[]; placements: ResolvedPlacementRecord[]; selectedFacility: string | null; onSelectFacility: (id: string | null) => void; routeFacilityId: string | null;
  expandedFacility: string | null; onExpandFacility: (id: string | null) => void; onShowRoute: (id: string) => void;
  filters: PlacementFilters; drawerFacilityId: string | null; onDrawerOpen: (id: string | null) => void; activeSkills: string[]; showLegend: boolean;
}) {
  const { candidate } = data;
  const mapRef = useRef<L.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const markersRef = useRef<L.LayerGroup | null>(null);
  const skillMarkersRef = useRef<L.LayerGroup | null>(null);
  const routeLayerRef = useRef<L.LayerGroup | null>(null);
  const tileLayerRef = useRef<L.TileLayer | null>(null);
  const pendingClickRef = useRef<{ facilityId: string; timeout: ReturnType<typeof setTimeout> } | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [mapStyle, setMapStyle] = useState<keyof typeof MAP_STYLES>("light");

  const allSkills = useMemo(() => [...candidate.skills, ...candidate.certifications], [candidate]);

  const filteredFacilities = useMemo(() => {
    return facilities.filter(f => {
      const distance = calculateDistance(candidate.lat, candidate.lng, f.lat, f.lng);
      const matching = matchingSkills(allSkills, f.requiredSkills);
      const placement = getPlacementForFacility(placements, f.id);
      if (filters.maxDistance < 100 && distance > filters.maxDistance) return false;
      if (filters.matchingOnly && matching.length === 0) return false;
      if (filters.status.length > 0 && placement && !filters.status.includes(placement.status)) return false;
      if (filters.status.length > 0 && !placement) return false;
      return true;
    });
  }, [filters, allSkills, facilities, candidate, placements]);

  const handleStyleChange = useCallback((style: keyof typeof MAP_STYLES) => {
    setMapStyle(style);
    if (mapRef.current && tileLayerRef.current) tileLayerRef.current.setUrl(MAP_STYLES[style].url);
  }, []);

  const handleSearchSelect = useCallback((id: string) => { onSelectFacility(id); onDrawerOpen(id); }, [onSelectFacility, onDrawerOpen]);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    const map = L.map(mapContainerRef.current, { center: [candidate.lat, candidate.lng], zoom: 13, zoomControl: false, attributionControl: false });
    L.control.zoom({ position: "bottomright" }).addTo(map);
    const tileLayer = L.tileLayer(MAP_STYLES[mapStyle].url, { maxZoom: 19 }).addTo(map);
    tileLayerRef.current = tileLayer;
    L.control.attribution({ position: "bottomleft", prefix: false }).addAttribution('&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>').addTo(map);
    mapRef.current = map;
    const skillPane = map.createPane("skillPins");
    skillPane.style.zIndex = "650";
    markersRef.current = L.layerGroup().addTo(map);
    skillMarkersRef.current = L.layerGroup().addTo(map);
    routeLayerRef.current = L.layerGroup().addTo(map);
    const allPoints: L.LatLngExpression[] = [[candidate.lat, candidate.lng], ...facilities.map(f => [f.lat, f.lng] as L.LatLngExpression)];
    if (allPoints.length > 1) {
      map.fitBounds(L.latLngBounds(allPoints as L.LatLngTuple[]), { padding: [60, 60], maxZoom: 14 });
    }
    setMapReady(true);
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    if (!mapReady || !markersRef.current || !mapRef.current) return;
    markersRef.current.clearLayers();
    const candidateMarker = L.marker([candidate.lat, candidate.lng], { icon: createCandidateIcon(), zIndexOffset: 1000 });
    candidateMarker.bindPopup(`<div style="font-family:system-ui;min-width:180px;"><div style="font-weight:600;font-size:14px;color:#1e293b;">${candidate.firstName} ${candidate.lastName}</div><div style="font-size:12px;color:#64748b;margin-top:2px;">${candidate.title}</div><div style="font-size:11px;color:#94a3b8;margin-top:4px;">${candidate.address}, ${candidate.city}</div></div>`);
    markersRef.current.addLayer(candidateMarker);
    filteredFacilities.forEach(facility => {
      const isSelected = selectedFacility === facility.id;
      const marker = L.marker([facility.lat, facility.lng], { icon: createFacilityIcon(facility, isSelected, activeSkills, candidate, placements), zIndexOffset: isSelected ? 500 : 0 });
      marker.on("click", () => {
        const prev = pendingClickRef.current;
        if (prev && prev.facilityId === facility.id) {
          clearTimeout(prev.timeout);
          pendingClickRef.current = null;
          onDrawerOpen(facility.id);
          return;
        }
        if (prev) clearTimeout(prev.timeout);
        pendingClickRef.current = {
          facilityId: facility.id,
          timeout: setTimeout(() => {
            pendingClickRef.current = null;
            onSelectFacility(facility.id);
            if (expandedFacility === facility.id) onExpandFacility(null);
            else onExpandFacility(facility.id);
          }, 250),
        };
      });
      markersRef.current!.addLayer(marker);
    });
    return () => {
      const prev = pendingClickRef.current;
      if (prev) {
        clearTimeout(prev.timeout);
        pendingClickRef.current = null;
      }
    };
  }, [mapReady, filteredFacilities, selectedFacility, expandedFacility, allSkills, activeSkills, onSelectFacility, onExpandFacility, onDrawerOpen, candidate, placements]);

  const renderSkillPins = useCallback(() => {
    if (!skillMarkersRef.current || !mapRef.current) return;
    skillMarkersRef.current.clearLayers();
    if (!expandedFacility) return;
    const facility = facilities.find(f => f.id === expandedFacility);
    if (!facility) return;
    const placement = getPlacementForFacility(placements, facility.id);
    const skills = placement ? placement.skills : facility.requiredSkills;
    skills.forEach((skill, i) => {
      const pin = createSkillPin(skill, i, skills.length, facility.lat, facility.lng, mapRef.current!);
      const marker = L.marker([pin.lat, pin.lng], { icon: pin.icon, pane: "skillPins" });
      const line = L.polyline([[facility.lat, facility.lng], [pin.lat, pin.lng]], { color: pin.color, weight: 1.5, opacity: 0.5, dashArray: "4 4", pane: "skillPins" });
      marker.bindPopup(`<div style="font-family:system-ui;text-align:center;"><div style="font-weight:600;font-size:13px;color:${pin.color};">${pin.skill}</div><div style="font-size:11px;color:#64748b;margin-top:2px;">${facility.name}</div></div>`);
      skillMarkersRef.current!.addLayer(line);
      skillMarkersRef.current!.addLayer(marker);
    });
  }, [expandedFacility, facilities, placements]);
  useEffect(() => {
    if (!mapReady) return;
    renderSkillPins();
  }, [mapReady, renderSkillPins]);
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    const map = mapRef.current;
    const onZoom = () => renderSkillPins();
    map.on("zoomend", onZoom);
    return () => { map.off("zoomend", onZoom); };
  }, [mapReady, renderSkillPins]);

  useEffect(() => {
    if (!mapReady || !routeLayerRef.current || !mapRef.current) return;
    routeLayerRef.current.clearLayers();
    if (!routeFacilityId) return;
    const facility = facilities.find(f => f.id === routeFacilityId);
    if (!facility) return;
    const straightDist = calculateDistance(candidate.lat, candidate.lng, facility.lat, facility.lng);
    const midLat = (candidate.lat + facility.lat) / 2;
    const midLng = (candidate.lng + facility.lng) / 2;
    const loadingLabel = L.marker([midLat, midLng], {
      icon: L.divIcon({ className: "distance-label", iconSize: [120, 36], iconAnchor: [60, 18], html: `<div style="background:white;border:1px solid #e2e8f0;border-radius:18px;padding:6px 12px;font-size:11px;font-weight:600;color:#94a3b8;text-align:center;box-shadow:0 2px 8px rgba(0,0,0,0.1);white-space:nowrap;">Loading route...</div>` }),
      interactive: false,
    });
    routeLayerRef.current.addLayer(loadingLabel);
    fetchDrivingRoute(candidate.lat, candidate.lng, facility.lat, facility.lng).then(route => {
      if (!routeLayerRef.current || !mapRef.current) return;
      routeLayerRef.current.clearLayers();
      let displayDist: number; let displayTime: string; let routePoints: [number, number][];
      if (route) {
        displayDist = route.distanceMiles;
        displayTime = route.durationMinutes >= 60 ? `${Math.floor(route.durationMinutes / 60)}h ${route.durationMinutes % 60}m drive` : `${route.durationMinutes} min drive`;
        routePoints = route.coordinates;
        routeLayerRef.current.addLayer(L.polyline(routePoints, { color: "#3B82F6", weight: 8, opacity: 0.15, lineCap: "round", lineJoin: "round" }));
        routeLayerRef.current.addLayer(L.polyline(routePoints, { color: "#3B82F6", weight: 4, opacity: 0.8, lineCap: "round", lineJoin: "round" }));
      } else {
        displayDist = straightDist;
        displayTime = `~${Math.round(straightDist * 2.5)} min est.`;
        routeLayerRef.current.addLayer(L.polyline([[candidate.lat, candidate.lng], [facility.lat, facility.lng]], { color: "#3B82F6", weight: 3, opacity: 0.6, dashArray: "8 6" }));
      }
      const rMidLat = (candidate.lat + facility.lat) / 2;
      const rMidLng = (candidate.lng + facility.lng) / 2;
      routeLayerRef.current.addLayer(L.marker([rMidLat, rMidLng], {
        icon: L.divIcon({ className: "distance-label", iconSize: [160, 36], iconAnchor: [80, 18], html: `<div style="background:white;border:1px solid #e2e8f0;border-radius:18px;padding:6px 12px;font-size:11px;font-weight:600;color:#1e293b;text-align:center;box-shadow:0 2px 8px rgba(0,0,0,0.1);white-space:nowrap;display:flex;align-items:center;gap:6px;justify-content:center;"><span style="color:#3B82F6;">${displayDist} mi</span><span style="color:#cbd5e1;">|</span><span style="color:#64748b;">${displayTime}</span></div>` }),
        interactive: false,
      }));
      mapRef.current!.fitBounds(L.latLngBounds([candidate.lat, candidate.lng], [facility.lat, facility.lng]), { padding: [80, 80] });
    });
  }, [mapReady, routeFacilityId, candidate, facilities]);

  useEffect(() => {
    if (!mapReady || !mapRef.current || !selectedFacility) return;
    const facility = facilities.find(f => f.id === selectedFacility);
    if (!facility) return;
    const bounds = L.latLngBounds(
      [candidate.lat, candidate.lng],
      [facility.lat, facility.lng]
    );
    const dist = mapRef.current.distance([candidate.lat, candidate.lng], [facility.lat, facility.lng]);
    if (dist > 5000) {
      mapRef.current.flyToBounds(bounds, { padding: [80, 80], maxZoom: 14, duration: 0.8 });
    } else {
      mapRef.current.flyTo([facility.lat, facility.lng], 15, { duration: 0.8 });
    }
  }, [mapReady, selectedFacility, facilities, candidate]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }} data-testid="map-container">
      <div ref={mapContainerRef} style={{ width: "100%", height: "100%" }} />
      <MapControlsSection facilities={facilities} candidate={candidate} onSelectFacility={handleSearchSelect} onStyleChange={handleStyleChange} currentStyle={mapStyle} />
      {showLegend && (
        <div style={{ position: "absolute", top: 16, left: 16, zIndex: 500, backgroundColor: "rgba(255,255,255,0.95)", backdropFilter: "blur(4px)", borderRadius: 8, boxShadow: "0 10px 15px rgba(0,0,0,0.1)", padding: 12, maxWidth: 220 }} data-testid="map-legend">
          <h4 style={{ fontSize: 10, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 8px" }}>Legend</h4>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={{ width: 10, height: 10, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #4f46e5)", border: "1px solid #fff", boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }} /><span style={{ fontSize: 10, color: "#475569" }}>Candidate Home</span></div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={{ width: 10, height: 10, borderRadius: "50%", border: "2px solid #059669", backgroundColor: "#fff" }} /><span style={{ fontSize: 10, color: "#475569" }}>Active</span></div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={{ width: 10, height: 10, borderRadius: "50%", border: "2px solid #d97706", backgroundColor: "#fff" }} /><span style={{ fontSize: 10, color: "#475569" }}>Pending</span></div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={{ width: 10, height: 10, borderRadius: "50%", border: "2px solid #94a3b8", backgroundColor: "#fff" }} /><span style={{ fontSize: 10, color: "#475569" }}>Completed</span></div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={{ width: 10, height: 10, borderRadius: "50%", border: "2px solid #ef4444", backgroundColor: "#fff" }} /><span style={{ fontSize: 10, color: "#475569" }}>Cancelled</span></div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={{ width: 10, height: 10, borderRadius: "50%", border: "2px solid #3b82f6", backgroundColor: "#fff" }} /><span style={{ fontSize: 10, color: "#475569" }}>Available</span></div>
          </div>
          <p style={{ fontSize: 9, color: "#94a3b8", marginTop: 6, marginBottom: 0 }}>Click pin to explode skill breakdown</p>
        </div>
      )}
      {drawerFacilityId && <FacilityDrawerSection facilityId={drawerFacilityId} candidate={candidate} facilities={facilities} placements={placements} onClose={() => onDrawerOpen(null)} onShowRoute={onShowRoute} />}
      <style>{`
        @keyframes candidatePulse { 0%, 100% { transform: scale(1); opacity: 0.6; } 50% { transform: scale(1.5); opacity: 0; } }
        @keyframes skillHighlightPulse { 0%, 100% { transform: scale(1); opacity: 0.8; } 50% { transform: scale(1.15); opacity: 0.4; } }
        .custom-facility-marker, .custom-candidate-marker, .skill-pin, .distance-label { background: transparent !important; border: none !important; overflow: visible !important; }
        .leaflet-popup-content-wrapper { border-radius: 12px !important; box-shadow: 0 4px 16px rgba(0,0,0,0.12) !important; border: 1px solid #e2e8f0 !important; }
        .leaflet-popup-tip { box-shadow: none !important; border: 1px solid #e2e8f0 !important; border-top: none !important; border-left: none !important; }
      `}</style>
    </div>
  );
}

function createFacilityIcon(facility: ResolvedFacilityInfo, isSelected: boolean, activeSkills: string[], candidate: ResolvedCandidateInfo, placements: ResolvedPlacementRecord[]): L.DivIcon {
  const allSkills = [...candidate.skills, ...candidate.certifications];
  const matching = matchingSkills(allSkills, facility.requiredSkills);
  const placement = getPlacementForFacility(placements, facility.id);
  const statusColor = placement ? getStatusColor(placement.status) : "#3B82F6";
  const hasActiveFilter = activeSkills.length > 0;
  const activeMatching = hasActiveFilter
    ? activeSkills.filter(as => facility.requiredSkills.some(fs => skillMatchesByDisciplineSpecialty(as, fs)))
    : [];
  const isHighlighted = !hasActiveFilter || activeMatching.length > 0;
  const isDimmed = hasActiveFilter && activeMatching.length === 0;
  const size = isSelected ? 44 : isHighlighted && hasActiveFilter ? 42 : 36;
  const borderWidth = isSelected ? 3 : isHighlighted && hasActiveFilter ? 3 : 2;
  const glowColor = isHighlighted && hasActiveFilter && activeMatching.length > 0 ? getSkillColor(activeMatching[0]) : statusColor;
  const orderPos = placement?.orderPositions ?? 0;
  const facilityPos = facility.openPositions ?? 0;
  const chipParts: string[] = [];
  if (facilityPos > 0) chipParts.push(`F:${facilityPos}`);
  if (orderPos > 0) chipParts.push(`O:${orderPos}`);
  const chipText = chipParts.length > 0 ? chipParts.join(" ") : "";
  const chipColor = isHighlighted && hasActiveFilter ? glowColor : "#2563EB";
  return L.divIcon({
    className: "custom-facility-marker", iconSize: [size, size + 12], iconAnchor: [size / 2, size + 12], popupAnchor: [0, -(size + 12)],
    html: `<div style="position:relative;width:${size}px;height:${size + 12}px;cursor:pointer;${isDimmed ? "opacity:0.25;filter:grayscale(80%);" : ""}transition:all 0.3s ease;">
      ${isHighlighted && hasActiveFilter ? `<div style="position:absolute;top:-4px;left:-4px;right:-4px;bottom:8px;border-radius:50%;background:${glowColor}20;box-shadow:0 0 12px ${glowColor}40;animation:skillHighlightPulse 2s ease-in-out infinite;"></div>` : ""}
      <div style="width:${size}px;height:${size}px;background:white;border:${borderWidth}px solid ${isHighlighted && hasActiveFilter ? glowColor : statusColor};border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:${isHighlighted && hasActiveFilter ? `0 2px 12px ${glowColor}50` : "0 2px 8px rgba(0,0,0,0.15)"};transition:all 0.3s ease;${isSelected ? "transform:scale(1.1);box-shadow:0 4px 16px rgba(0,0,0,0.25);" : ""}">
        <svg width="${size * 0.45}" height="${size * 0.45}" viewBox="0 0 24 24" fill="none" stroke="${isHighlighted && hasActiveFilter ? glowColor : statusColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/></svg>
      </div>
      <div style="width:0;height:0;border-left:8px solid transparent;border-right:8px solid transparent;border-top:12px solid ${isHighlighted && hasActiveFilter ? glowColor : statusColor};position:absolute;top:${size - 3}px;left:50%;transform:translateX(-50%);transition:all 0.3s ease;"></div>
      ${chipText ? `<div style="position:absolute;top:-6px;left:${size - 6}px;background:${chipColor};color:white;border-radius:9px;font-size:9px;font-weight:600;display:flex;align-items:center;gap:3px;padding:2px 6px;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.2);transition:all 0.3s ease;white-space:nowrap;height:16px;line-height:16px;">${chipText}</div>` : ""}
    </div>`,
  });
}

function createCandidateIcon(): L.DivIcon {
  return L.divIcon({
    className: "custom-candidate-marker", iconSize: [48, 48], iconAnchor: [24, 24],
    html: `<div style="width:48px;height:48px;position:relative;"><div style="position:absolute;inset:0;background:rgba(59,130,246,0.15);border-radius:50%;animation:candidatePulse 2s ease-in-out infinite;"></div><div style="position:absolute;inset:8px;background:linear-gradient(135deg,#3B82F6,#6366F1);border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(59,130,246,0.4);display:flex;align-items:center;justify-content:center;"><svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></div></div>`,
  });
}

function createSkillPin(skill: string, index: number, total: number, centerLat: number, centerLng: number, map: L.Map) {
  const startAngle = Math.PI / 2;
  const angle = startAngle + (index / total) * 2 * Math.PI;
  const spreadPx = 65;
  const centerPoint = map.latLngToLayerPoint(L.latLng(centerLat, centerLng));
  const pinPoint = L.point(centerPoint.x + spreadPx * Math.cos(angle), centerPoint.y + spreadPx * Math.sin(angle));
  const pinLatLng = map.layerPointToLatLng(pinPoint);
  const color = getSkillColor(skill);
  return {
    lat: pinLatLng.lat,
    lng: pinLatLng.lng,
    icon: L.divIcon({
      className: "skill-pin", iconSize: [28, 28], iconAnchor: [14, 14],
      html: `<div style="width:28px;height:28px;background:${color};border-radius:50%;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.2);display:flex;align-items:center;justify-content:center;font-size:8px;font-weight:700;color:white;cursor:pointer;transition:transform 0.2s ease;" title="${skill}">${skill.slice(0, 3)}</div>`,
    }),
    skill, color,
  };
}

export interface PlacementTrackerProps {
  data: PlacementData;
  /** When true, uses 100% width/height to fit within parent (e.g. embedded in tab). Avoids 100vw overflow. */
  embedded?: boolean;
  /** When true, opens in maximized (full-screen overlay) view on mount. */
  openMaximized?: boolean;
}

export default function PlacementTracker({ data, embedded = false, openMaximized = false }: PlacementTrackerProps) {
  useMemo(() => { dynamicSkillColors = buildSkillColorMap(data); }, [data]);
  const sizeStyle = embedded ? { width: '100%', height: '100%' } : { width: '100vw', height: '100vh' };
  const [resolvedData, setResolvedData] = useState<ResolvedPlacementData | null>(null);
  const [geocodeProgress, setGeocodeProgress] = useState("");
  const [selectedFacility, setSelectedFacility] = useState<string | null>(null);
  const [routeFacilityId, setRouteFacilityId] = useState<string | null>(null);
  const [expandedFacility, setExpandedFacility] = useState<string | null>(null);
  const [drawerFacilityId, setDrawerFacilityId] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(true);
  const [showAnalytics, setShowAnalytics] = useState(true);
  const [showTimeline, setShowTimeline] = useState(true);
  const [showLegend, setShowLegend] = useState(true);
  const [activeSkills, setActiveSkills] = useState<string[]>([]);
  const [filters, setFilters] = useState<PlacementFilters>({ status: [], maxDistance: 100, sortBy: "nearest", matchingOnly: false });

  const [geocodeError, setGeocodeError] = useState(false);
  const [isMaximized, setIsMaximized] = useState(openMaximized);

  const toggleMaximize = useCallback(() => setIsMaximized(prev => !prev), []);

  const runGeocode = useCallback(() => {
    setResolvedData(null);
    setGeocodeError(false);
    setGeocodeProgress("Resolving addresses...");
    resolveAllCoordinates(data).then(resolved => {
      setResolvedData(resolved);
      setGeocodeProgress("");
    }).catch(() => {
      setGeocodeError(true);
      setGeocodeProgress("Failed to resolve addresses.");
    });
  }, [data]);

  useEffect(() => { runGeocode(); }, [runGeocode]);

  const facilities = useMemo(() => resolvedData ? deriveFacilities(resolvedData.placements) : [], [resolvedData]);

  if (!resolvedData) {
    return (
      <div style={{ ...sizeStyle, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", backgroundColor: "#fff", fontFamily: "'Inter', system-ui, -apple-system, sans-serif", gap: 16 }} data-testid="loading-screen">
        {!geocodeError && <div style={{ width: 40, height: 40, border: "3px solid #e2e8f0", borderTopColor: "#3b82f6", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />}
        <p style={{ fontSize: 14, color: geocodeError ? "#dc2626" : "#64748b", margin: 0 }}>{geocodeProgress || "Loading..."}</p>
        <p style={{ fontSize: 12, color: "#94a3b8", margin: 0 }}>Geocoding {data.placements.length + 1} addresses</p>
        {geocodeError && <Button variant="contained" size="small" sx={{ textTransform: "none", mt: 1 }} onClick={runGeocode} data-testid="button-retry-geocode">Retry</Button>}
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const handleSelectFacility = (id: string | null) => {
    setSelectedFacility(id);
    if (id && id !== expandedFacility) setExpandedFacility(id);
    else if (id === null) setExpandedFacility(null);
  };
  const handleShowRoute = (facilityId: string) => setRouteFacilityId(routeFacilityId === facilityId ? null : facilityId);
  const handleDrawerOpen = (id: string | null) => setDrawerFacilityId(id);

  const maximizedWrapperStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    width: "100vw",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#fff",
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  };

  const content = (
    <div
      style={{
        ...(isMaximized ? { width: "100%", height: "100%", minHeight: 0 } : sizeStyle),
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fff",
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        ...(embedded && !isMaximized ? { overflowY: "auto", overflowX: "hidden", width: "100%" } : {}),
      }}
      data-testid="page-home"
    >
      <header style={{ height: 56, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "0 12px", borderBottom: "1px solid #f1f5f9", backgroundColor: "#fff", zIndex: 50 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Button variant="text" size="small"
            sx={{ fontSize: 12, gap: 0.5, textTransform: "none", color: "#334155", "&:hover": { backgroundColor: "#f1f5f9" } }}
            onClick={() => setPanelOpen(!panelOpen)} data-testid="button-toggle-panel"
            aria-label={panelOpen ? "Hide details" : "Show details"}
          >
            {panelOpen ? <EyeOff size={14} /> : <Eye size={14} />}
            {panelOpen ? "Hide details" : "Show details"}
          </Button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Button variant={showAnalytics ? "contained" : "text"} size="small"
            sx={{ fontSize: 12, gap: 0.5, textTransform: "none", backgroundColor: showAnalytics ? "#f1f5f9" : "transparent", color: showAnalytics ? "#334155" : "#64748b", boxShadow: "none", "&:hover": { backgroundColor: "#e2e8f0", boxShadow: "none" } }}
            onClick={() => setShowAnalytics(!showAnalytics)} data-testid="button-toggle-analytics">
            <BarChart3 size={14} />Analytics
          </Button>
          <Button variant={showTimeline ? "contained" : "text"} size="small"
            sx={{ fontSize: 12, gap: 0.5, textTransform: "none", backgroundColor: showTimeline ? "#f1f5f9" : "transparent", color: showTimeline ? "#334155" : "#64748b", boxShadow: "none", "&:hover": { backgroundColor: "#e2e8f0", boxShadow: "none" } }}
            onClick={() => setShowTimeline(!showTimeline)} data-testid="button-toggle-timeline">
            <Clock size={14} />Timeline
          </Button>
          <Button variant={showLegend ? "contained" : "text"} size="small"
            sx={{ fontSize: 12, gap: 0.5, textTransform: "none", backgroundColor: showLegend ? "#f1f5f9" : "transparent", color: showLegend ? "#334155" : "#64748b", boxShadow: "none", "&:hover": { backgroundColor: "#e2e8f0", boxShadow: "none" } }}
            onClick={() => setShowLegend(!showLegend)} data-testid="button-toggle-legend">
            <Info size={14} />Legend
          </Button>
          {routeFacilityId && (
            <Button variant="contained" size="small" color="error" sx={{ gap: 0.5, textTransform: "none" }} onClick={() => setRouteFacilityId(null)} data-testid="button-clear-route">
              <X size={12} />Clear Route
            </Button>
          )}
          <Button variant="text" size="small"
            sx={{ fontSize: 12, gap: 0.5, textTransform: "none", color: "#334155", "&:hover": { backgroundColor: "#f1f5f9" } }}
            onClick={toggleMaximize} data-testid="button-toggle-fullscreen"
            aria-label={isMaximized ? "Normal size" : "Maximize"}
          >
            {isMaximized ? <Minimize2 size={14} /> : <Maximize size={14} />}
            {isMaximized ? "Normal size" : "Maximize"}
          </Button>
        </div>
      </header>
      {showAnalytics && <AnalyticsStripSection data={resolvedData} facilities={facilities} />}
      <div style={{ flex: 1, display: "flex", position: "relative", overflow: "hidden", minHeight: 0, ...(embedded ? { overflowX: "hidden" } : {}) }}>
        {panelOpen && <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.3)", zIndex: 30 }} className="panel-overlay-mobile" onClick={() => setPanelOpen(false)} data-testid="panel-overlay" />}
        <div style={{ flexShrink: 0, borderRight: "1px solid #f1f5f9", backgroundColor: "#fff", transition: "all 0.3s ease-in-out", zIndex: 40, width: panelOpen ? (embedded ? 300 : 380) : 0, overflow: panelOpen ? "visible" : "hidden" }} className="side-panel-container" data-testid="side-panel">
          <div style={{ width: embedded ? 300 : 380, height: "100%", minWidth: embedded ? 300 : 380 }}>
            <CandidatePanelSection data={resolvedData} facilities={facilities} placements={resolvedData.placements} selectedFacility={selectedFacility} onSelectFacility={handleSelectFacility}
              onShowRoute={handleShowRoute} onDrawerOpen={handleDrawerOpen} filters={filters} onFiltersChange={setFilters} activeSkills={activeSkills} onActiveSkillsChange={setActiveSkills} embedded={embedded} />
          </div>
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, minWidth: embedded ? 280 : 0 }} data-testid="map-area">
          <div style={{ flex: 1, position: "relative" }}>
            <MapSection key={resolvedData.candidate.id} data={resolvedData} facilities={facilities} placements={resolvedData.placements} selectedFacility={selectedFacility} onSelectFacility={handleSelectFacility}
              routeFacilityId={routeFacilityId} expandedFacility={expandedFacility} onExpandFacility={setExpandedFacility} onShowRoute={handleShowRoute}
              filters={filters} drawerFacilityId={drawerFacilityId} onDrawerOpen={handleDrawerOpen} activeSkills={activeSkills} showLegend={showLegend} />
          </div>
          {showTimeline && <TimelineSection placements={resolvedData.placements} onSelectFacility={(id) => { handleSelectFacility(id); handleDrawerOpen(id); }} />}
        </div>
      </div>
      <style>{`
        @media (max-width: 768px) {
          .side-panel-container { position: fixed !important; top: 56px !important; left: 0 !important; bottom: 0 !important; width: ${panelOpen ? "85vw" : "0"} !important; }
          .side-panel-container > div { width: 85vw !important; }
        }
        @media (min-width: 769px) {
          .panel-overlay-mobile { display: none !important; }
        }
      `}</style>
    </div>
  );

  if (isMaximized) {
    return ReactDOM.createPortal(
      <div style={maximizedWrapperStyle}>{content}</div>,
      document.body
    );
  }
  return content;
}
